import {FC, useMemo, useState} from 'react'
import {MenuButton, MenuProvider, useDialogStore} from '@ariakit/react'
import {Button} from '@components/ui/button/Button.tsx'
import {CheckIcon, DotsHorizontalIcon, ListCheckboxIcon, QueueListIcon, UserX01Icon} from '@components/ui/icon/Icon.tsx'
import {Menu, MenuItem} from '@components/ui/menu-atoms/MenuAtoms.tsx'
import {HttpGetSignatureSheetsResponse} from '../../http/accesses.http'
import {generatePath, Link} from 'react-router-dom'
import {routes} from '@/utilities/constants/routes'
import {useTranslation} from '@/translations/i18n'
import {QUERY_KEYS} from '@/queryClient'
import {toast} from '@/utilities/toast/toast'
import {useQueryClient} from '@tanstack/react-query'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useValidateAccesses} from '../../queries/useValidateAccesses'
import {CreateAccessDialog} from '../create-access-dialog/CreateAccessDialog'
import {CreateAbsenceDialog} from '../create-absence-dialog/CreateAbsenceDialog'
import {CreateAccessFormValidationSchema} from '../../formSchemas'
import {usePermissions} from '@/features/auth/store/store'

export const AccessesValidatorOtherActionMenu: FC<{
    signatureSheet: HttpGetSignatureSheetsResponse[0]
}> = ({signatureSheet}) => {
    const [isOpen, setOpen] = useState(false)
    const createAccessDialogStore = useDialogStore()
    const createAbsenceDialogStore = useDialogStore()
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    const permissions = usePermissions()
    const accessesToValidate = signatureSheet.accesses.filter(access => access.status === 'to_validate')
    const {mutate: validateAccesses, isPending} = useValidateAccesses({
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES_SIGNATURE_SHEETS]})
            toast.success(t('accesses:access_validated_success', {count: accessesToValidate.length}))
        }
    })

    const access = signatureSheet.accesses[0]

    const accessDefaultValues: Partial<CreateAccessFormValidationSchema> = useMemo(() => {
        const patient =
            access?.patientId && access?.event.eventPatients?.[0]?.patient?.fullName
                ? {
                      id: access.patientId,
                      fullName: access.event.eventPatients?.[0]?.patient?.fullName
                  }
                : undefined

        const project =
            access?.projectId && access?.project?.sector && access?.patient?.serviceType
                ? {
                      id: access.projectId,
                      sector: access.project.sector,
                      patient: {serviceType: access.patient.serviceType},
                      code: access.project.code,
                      startDate: access.project.startDate,
                      expirationDate: access.project.expirationDate,
                      assistiveRegime: access.project.assistiveRegime
                  }
                : undefined

        const consultant =
            access?.consultantId && access?.event.eventConsultants?.[0]?.user?.fullName
                ? {
                      id: access.consultantId,
                      fullName: access.event.eventConsultants?.[0]?.user?.fullName,
                      specializationName: access.event.eventConsultants?.[0]?.user?.specialization,
                      specializationId: access.event.eventConsultants?.[0]?.user?.specializationId
                  }
                : undefined
        return {
            patient,
            project,
            consultant
        }
    }, [access])

    const abnsences = useMemo(
        () => signatureSheet.accesses.filter(access => access.type === 'absence'),
        [signatureSheet]
    )

    return (
        <MenuProvider open={isOpen} setOpen={setOpen}>
            <MenuButton
                render={
                    <Button
                        className={isOpen ? undefined : 'show-on-hover'}
                        size="xs"
                        shape="square"
                        variant="secondary"
                        onClick={event => event.stopPropagation()}
                    >
                        <DotsHorizontalIcon />
                    </Button>
                }
            />
            <Menu width="220px" onClick={e => e.stopPropagation()}>
                {accessesToValidate.length > 0 &&
                    !abnsences.some(access => access.event.communicatedOnTime === null) && (
                        <MenuItem
                            onClick={() => validateAccesses({accessIds: accessesToValidate.map(access => access.id)})}
                        >
                            {isPending ? <Spinner /> : <CheckIcon />}
                            {t('accesses:validate_access', {count: accessesToValidate.length})}
                        </MenuItem>
                    )}
                <MenuItem onClick={() => createAccessDialogStore.setOpen(true)}>
                    <ListCheckboxIcon />
                    {t('accesses:add_access')}
                </MenuItem>

                <MenuItem onClick={() => createAbsenceDialogStore.setOpen(true)}>
                    <UserX01Icon />
                    {t('accesses:add_absence')}
                </MenuItem>

                {access && permissions?.projects?.read && (
                    <MenuItem
                        render={
                            <Link
                                to={generatePath(routes.PROJECT_DETAILS_PLANNED_ACCESSES.handle.path, {
                                    id: access.projectId
                                })}
                            >
                                <QueueListIcon />
                                {t('accesses:title_pri_accesses_absences')}
                            </Link>
                        }
                    />
                )}
            </Menu>
            <CreateAccessDialog defaultValues={accessDefaultValues} dialogStore={createAccessDialogStore} />
            <CreateAbsenceDialog defaultValues={accessDefaultValues} dialogStore={createAbsenceDialogStore} />
        </MenuProvider>
    )
}
